import InputUrl from '@/components/Modals/ExistedFilesModal/UploadNewFiles/InputUrl';
import { CommonFileType, ExtendedFile } from '@/types';
import { isAudio } from '@/utils';
import { PlusOutlined } from '@ant-design/icons';
import { Col, Row, message } from 'antd';
import { FC, useEffect } from 'react';
import { Accept, FileError, useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';

import AudioItem from './AudioItem';
import ImageItem from './ImageItem';

import './styles.scss';

type FileUploadProps = {
  value?: CommonFileType[] | CommonFileType;
  accept?: Accept;
  multiple?: boolean;
  placeholder?: string;
  onChange?: (newFiles: CommonFileType[]) => void;
  showInputUrl?: boolean;
};

const FileUploaderField: FC<FileUploadProps> = (props: FileUploadProps) => {
  const {
    onChange,
    value = [],
    accept,
    multiple = true,
    placeholder = "Drag 'n' drop some files here, or click to select files",
    showInputUrl = true,
  } = props;

  const valueL = Array.isArray(value) ? value : [value];
  useEffect(() => {
    const converted = (Array.isArray(value) ? value : [value]).map((file) => {
      if (!file.uniqueId) {
        return {
          ...file,
          uniqueId: uuidv4(),
        };
      }
      return file;
    });

    if (onChange) {
      onChange(converted);
    }
  }, []);

  const attachFiles = (fs: CommonFileType[]) => {
    const newFiles = [...fs];
    if (multiple) {
      newFiles.push(...valueL);
    }
    newFiles.map((file) => {
      if (!file.uniqueId) {
        file.uniqueId = uuidv4();
      }
      return file;
    });

    if (onChange) {
      onChange(newFiles);
    }
  };

  const specialCharInFileNameValidator = (file: File) => {
    if (file.name.search(/[!@#$%^&*,?":{}|<>]/) > 0) {
      return {
        code: 'name-contain-special-char',
        message: `Please upload the file ${file.name} without special characters.`,
      };
    }

    return null;
  };

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    accept,
    onDrop: (acceptedFiles: CommonFileType[], fileRejectionsOnDrop) => {
      if (!fileRejectionsOnDrop.length) {
        const file = acceptedFiles.map((f: CommonFileType) =>
          Object.assign(f, {
            preview: URL.createObjectURL(f as ExtendedFile),
          })
        );

        attachFiles(file);
      }
    },
    multiple,
    validator: specialCharInFileNameValidator,
  });

  useEffect(() => {
    if (fileRejections.length) {
      const fileErrors: FileError[] = fileRejections.map(
        (fileRejection) => fileRejection.errors[0]
      );

      // remove duplicate file error
      const errorCodes = fileErrors.map((fileError: FileError) => fileError.code);
      const fileErrorsRemovedDup = fileErrors.filter(
        ({ code }, index) => !errorCodes.includes(code, index + 1)
      );

      // show message error
      fileErrorsRemovedDup.forEach((item) => message.error(item.message));
    }
  }, [fileRejections]);

  const removeFile = (fileUniqueId: string) => {
    if (onChange) {
      onChange(valueL.filter((f: CommonFileType) => f.uniqueId !== fileUniqueId));
    }
  };

  return (
    <Row>
      {showInputUrl && (
        <Col span={24}>
          <InputUrl onSubmit={attachFiles} />
        </Col>
      )}
      <Col span={24}>
        <div className="cvta-content-upload-image">
          {!Array.isArray(valueL) || valueL.length === 0 ? (
            <div {...getRootProps({ className: 'dropzone' })}>
              <input {...getInputProps()} />
              <p>{placeholder}</p>
            </div>
          ) : (
            <div className="image-container">
              {valueL.map((file: CommonFileType) => {
                if (isAudio(file.name || '')) {
                  return <AudioItem key={file.uniqueId} {...{ audioFile: file, removeFile }} />;
                }

                return <ImageItem key={file.uniqueId} {...{ img: file, removeFile }} />;
              })}
              {multiple && (
                <div {...getRootProps({ className: 'dropzone dropzone-type-image' })}>
                  <input {...getInputProps()} />
                  <PlusOutlined />
                  <div>Upload</div>
                </div>
              )}
            </div>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default FileUploaderField;
